import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { slotsFor } from '../data/booking';
import * as availability from '../services/availability';
import { getAccessToken } from '../services/client';

/**
 * Which times on the open day are already full.
 *
 * ---------------------------------------------------------------------------
 * IT ANSWERS IN GRID LABELS, NOT INSTANTS
 * ---------------------------------------------------------------------------
 * The server sends start instants (see `services/availability`), and the sheet
 * draws labels - "10:00 AM". The mapping belongs here rather than in either of
 * them: the service should not know how the grid is worded, and the sheet
 * should not do timezone arithmetic to colour a tile.
 *
 * Matched on epoch milliseconds, never on string equality. Two correct ISO
 * strings for the same moment can differ in offset spelling and in whether
 * they carry milliseconds, and comparing them as text closes the wrong slots
 * or none at all.
 *
 * ---------------------------------------------------------------------------
 * A FAILED CHECK DOES NOT MAKE SLOTS AVAILABLE
 * ---------------------------------------------------------------------------
 * A failed request means availability is unknown, not that every time is free
 * or fully booked. The sheet holds selection and confirmation until a retry
 * succeeds, including when a cached answer's refresh fails.
 */
export type UseAvailability = {
  /**
   * The hours this city actually opens and closes, when the server says so.
   *
   * Undefined means there is no opinion to have - either the answer has not
   * arrived, or `ops.dutyHours` is off for this deployment - and the caller
   * keeps its compiled grid. Deliberately NOT defaulted to a window here: a
   * default would be this app inventing opening hours again, which is the
   * thing being undone.
   */
  window?: availability.BookingWindow;
  /** Grid labels that can take no more work. Empty while unknown. */
  fullTimes: ReadonlySet<string>;
  /**
   * The subset of `fullTimes` a RULE closed, and which rule.
   *
   * Always a subset, never a separate set of tiles: the server puts every
   * blocked start in `fullSlots` as well. So the grid keeps one test for
   * "is this tile dead" and reads this only to say why.
   *
   * Empty against a server that does not send the field, which leaves the
   * tile dead and silent - exactly the behaviour that shipped.
   */
  blockedTimes: ReadonlyMap<string, availability.BlockedSlot>;
  /** Ops closed the shown day. Every time is also in `fullTimes`. */
  closed: boolean;
  /**
   * True while there is no answer at all for the shown day. A kept answer
   * being re-checked does not count: it is already on screen.
   */
  loading: boolean;
  /** A fresh request is pending, even when a cached grid is already visible. */
  checking: boolean;
  /**
   * True when the server was asked and could not answer.
   *
   * The sheet holds selection and confirmation and offers a retry.
   */
  failed: boolean;
  /** Re-check the current day without trusting its cached answer. */
  retry: () => void;
};

/**
 * What is being booked, which changes what counts as full: two services at
 * 11:00 need two professionals, a three-hour job three free hours, and a
 * booking being moved must not be counted against its own slot.
 */
export type AvailabilityScope = {
  serviceId?: string;
  items?: readonly availability.AvailabilityItem[];
  bookingId?: string;
};

export function useAvailability(
  /** The day being shown, or null when the picker is closed. */
  date: Date | null,
  addressId: string | undefined,
  scope: AvailabilityScope = {},
  /**
   * Bumped to force a re-read of a day already answered.
   *
   * The effect below is keyed on the DAY, so asking about a day it has just
   * answered is a no-op - which is right while somebody taps along the strip
   * and wrong the moment ops changes the city's opening hours underneath
   * them. Pull-to-refresh on the cart increments this; nothing else does.
   */
  reloadKey?: number,
): UseAvailability {
  const signedIn = !!getAccessToken();

  const [full, setFull] = useState<ReadonlySet<string>>(new Set());
  const [blocked, setBlocked] = useState<
    ReadonlyMap<string, availability.BlockedSlot>
  >(new Map());
  const [closed, setClosed] = useState(false);
  /*
   * Not cleared when the day changes, only replaced by the next answer, so
   * the grid does not collapse to the compiled one for a frame on every tap.
   *
   * It IS replaced by every answer, though - including one with no window.
   * Hours used to belong to the city alone; now ops can give one date its own
   * hours, so Monday's short day must not stay drawn once Tuesday is shown.
   */
  const [window, setWindow] = useState<availability.BookingWindow | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(
    Boolean(date && addressId && signedIn),
  );
  const [checking, setChecking] = useState(
    Boolean(date && addressId && signedIn),
  );
  const [failed, setFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retry = useCallback(() => {
    setFailed(false);
    setLoading(true);
    setChecking(true);
    setRetryCount(count => count + 1);
  }, []);

  /* Which request is current. The customer can tap along the day strip faster
     than these come back, and an earlier answer landing last would close
     yesterday's full slots on today's grid. */
  const attempt = useRef(0);

  /* A stable key, so tapping the SAME day twice does not refetch and a new
     `Date` object with identical value does not either. */
  const key = date ? availability.dateKey(date) : null;

  /* The scope as a value, so a basket rebuilt with the same contents on every
     render does not refetch, and a changed one does. */
  const scopeKey = JSON.stringify([
    scope.serviceId ?? null,
    scope.items ?? null,
    scope.bookingId ?? null,
  ]);

  /* The last pull-to-refresh this hook has honoured - see `reloadKey`. */
  const reloaded = useRef(reloadKey);
  const retried = useRef(retryCount);

  useEffect(() => {
    const mine = ++attempt.current;
    if (!key || !date || !addressId || !signedIn) {
      setFull(new Set());
      setBlocked(new Map());
      setClosed(false);
      setFailed(false);
      setLoading(false);
      setChecking(false);
      return;
    }

    const query: availability.AvailabilityQuery = {
      date: key,
      addressId,
      serviceId: scope.serviceId,
      items: scope.items,
      bookingId: scope.bookingId,
    };

    const apply = (day: availability.DayAvailability) => {
      /* This day's hours - see the note on `window`. */
      setWindow(day.window);
      setClosed(day.closed === true);
      /*
       * Instants in, labels out, against the grid this day is actually drawn
       * with - the city's window when it has one. The compiled 7:00 AM–8:00
       * PM grid was used here before, so a city open until 9:00 PM never
       * had its last two slots marked full.
       */
      const grid = slotsFor(day.window);
      setFull(availability.fullLabelsFor(date, day.fullSlots, grid));
      setBlocked(availability.blockedLabelsFor(date, day.blocked, grid));
    };

    /*
     * A kept answer is drawn at once, and asked for again underneath it so a
     * slot somebody took in the last minute still closes. With nothing kept,
     * the grid is emptied rather than left showing the PREVIOUS day's closed
     * times - which is what it did, for as long as the request took, and is
     * exactly when somebody taps a time - and `loading` puts the loader up.
     */
    const forced =
      reloaded.current !== reloadKey || retried.current !== retryCount;
    reloaded.current = reloadKey;
    retried.current = retryCount;
    const cached = forced ? undefined : availability.peekAvailability(query);
    setFailed(false);
    setChecking(true);
    if (cached) {
      apply(cached);
      setLoading(false);
    } else {
      setFull(new Set());
      setBlocked(new Map());
      setClosed(false);
      setLoading(true);
    }

    availability
      .loadAvailability(query, { fresh: forced || Boolean(cached) })
      .then(day => {
        if (mine === attempt.current) {
          apply(day);
        }
      })
      .catch(() => {
        if (mine !== attempt.current) {
          return;
        }
        setFailed(true);
      })
      .finally(() => {
        if (mine === attempt.current) {
          setLoading(false);
          setChecking(false);
        }
      });

    return () => {
      attempt.current += 1;
    };

    /* `date` and `scope` are intentionally absent: `key` and `scopeKey` are
       their stable identities, and depending on the objects would refetch on
       every render that rebuilt them. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, addressId, scopeKey, signedIn, reloadKey, retryCount]);

  return useMemo(
    () => ({
      window,
      fullTimes: full,
      blockedTimes: blocked,
      closed,
      loading,
      checking,
      failed,
      retry,
    }),
    [window, full, blocked, closed, loading, checking, failed, retry],
  );
}

export default useAvailability;
